import React, { useEffect } from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import RightSideModal from "../Modal/right-side-modal";
import InputField from "../form-fields/InputField";
import SelectField from "../form-fields/SelectField"; CustomRadio
import {
    ContractorService,
    ContractorInsuranceDetailsVM,
    ContractorLicenseDetailsVM,
    ContractorTaxDetailsVM,
    ContractorBusinessTypesEnum,
    DocumentEntityTypesEnum,
    DocumentService,
    DocumentTypesEnum,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import Card from "../card";
import PDFUploader from "../CustomUploader/PDFUploader";
import "../../newComponents/form-fields/form-ui.css";
import "../../newComponents/form-fields/Form.css";
import CustomRadio from "../form-fields/CustomRadio";
import PhoneInput from "../form-fields/PhoneInput";
import EmailInput from "../form-fields/EmailInput";
import { useFieldChangesSnapshot } from "../../hooks/useFieldChangesSnapshot";

// Props for the form component
interface ManageContractorFormProps {
    isOpen: boolean;
    onClose: () => void;
    entityId: number;
    defaultValues?: any | null;
    defaultDetailType?: string | null;
    refreshData: () => void;
    hasInsuranceDetails?: boolean;
    hasLicenseDetails?: boolean;
    hasTaxDetails?: boolean;
}

// Combined type for all possible form values
interface FormValues {
    detailType: string;
    // Insurance
    policyNumber?: string;
    insuranceValidUntil?: string;
    insuranceDocument?: File[];
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    // License
    licenseNumber?: string;
    licenseValidUntil?: string;
    licenseDocument?: File[];
    // Tax
    contractorBusinessTypesEnum: string; // Changed to string to work with CustomRadio
    taxId?: string;
    companyName?: string;
    suiteNo?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zip?: string;
}

const ManageContractorAdditionalDetailsForm: React.FC<ManageContractorFormProps> = ({
    isOpen,
    onClose,
    entityId,
    defaultValues,
    defaultDetailType,
    refreshData,
    hasInsuranceDetails,
    hasLicenseDetails,
    hasTaxDetails,
}) => {
    const [initialInsuranceFiles, setInitialInsuranceFiles] = React.useState<{ src: string; name: string }[]>([]);
    const [initialLicenseFiles, setInitialLicenseFiles] = React.useState<{ src: string; name: string }[]>([]);
    const [initialInsuranceDoc, setInitialInsuranceDoc] = React.useState<{ id: number, name: string } | null>(null);
    const [initialLicenseDoc, setInitialLicenseDoc] = React.useState<{ id: number, name: string } | null>(null);
    const [isDocLoading, setIsDocLoading] = React.useState(false);

    const methods = useForm<FormValues>({
        mode: "onChange",
        defaultValues: {
            detailType: "insurance",
            // 2. Set default as a string
            contractorBusinessTypesEnum: String(ContractorBusinessTypesEnum.Individual),
        },
    });
    const { reset, watch, handleSubmit, control, clearErrors, trigger, setValue, formState: { errors, isDirty, isValid, isSubmitting } } = methods;
    const detailType = watch("detailType");
    const businessType = watch("contractorBusinessTypesEnum");

    // isDirty alone is unreliable here -- this form hydrates itself from fetched data via
    // multiple setValue-equivalent paths (reset() + the async document-fetch effects), the
    // same class of bug documented in ui-consistency.md SS10 gotcha 9. Compounded by gotcha
    // 3's File-value sub-trap: insuranceDocument/licenseDocument are File-valued fields, and
    // RHF's deepEqual can't tell two File instances apart, so isDirty can get and stay stuck
    // true across an upload-then-revert cycle (bug/ui-bugfix-batch4 item 12).
    const { hasFieldChanges, setSnapshot } = useFieldChangesSnapshot<
        Omit<FormValues, "insuranceDocument" | "licenseDocument">
    >(control, [
        "detailType",
        "policyNumber",
        "insuranceValidUntil",
        "firstName",
        "lastName",
        "email",
        "phoneNumber",
        "licenseNumber",
        "licenseValidUntil",
        "contractorBusinessTypesEnum",
        "taxId",
        "companyName",
        "suiteNo",
        "streetAddress",
        "city",
        "state",
        "zip",
    ]);
    // dirtyFields.insuranceDocument/licenseDocument can't be used here: baseValues never
    // seeds those keys, so RHF's default is undefined, which never equals the post-upload
    // array value even after reverting to the original file. Compare against the actual
    // fetched document's name instead, mirroring the same check onSubmit already uses to
    // decide whether to re-upload.
    const watchedInsuranceDocument = useWatch({ control, name: "insuranceDocument" });
    const watchedLicenseDocument = useWatch({ control, name: "licenseDocument" });
    const hasInsuranceDocChanged = (watchedInsuranceDocument?.[0]?.name ?? null) !== (initialInsuranceDoc?.name ?? null);
    const hasLicenseDocChanged = (watchedLicenseDocument?.[0]?.name ?? null) !== (initialLicenseDoc?.name ?? null);
    const hasUnsavedChanges =
        (isDirty && hasFieldChanges) || hasInsuranceDocChanged || hasLicenseDocChanged;

    const selectedBusinessType = watch("contractorBusinessTypesEnum");

    const fetchAndSetDocument = async (documentId: number, setter: (files: any[]) => void, fileName: string) => {
        setIsDocLoading(true);
        try {
            const res = await DocumentService.getApiDocumentBase641(documentId);
            if (res?.success) {
                setter([{
                    src: `data:application/pdf;base64,${res.success}`,
                    name: fileName
                }]);
            }
        } catch (error) {
            console.error(`Error fetching ${fileName}:`, error);
        } finally {
            setIsDocLoading(false);
        }
    };

    useEffect(() => {
        clearErrors("taxId");      // old error remove karega
        trigger("taxId");          // new validation message apply karega
    }, [selectedBusinessType]);

    useEffect(() => {
        const initializeForm = async () => {
            if (isOpen) {
                const type = defaultDetailType || (!hasInsuranceDetails ? "insurance" : !hasLicenseDetails ? "license" : "tax");
                const baseValues = {
                    detailType: type,
                    policyNumber: "",
                    insuranceValidUntil: undefined,
                    firstName: "", lastName: "", email: "", phoneNumber: "",
                    licenseNumber: "",
                    licenseValidUntil: undefined,
                    // 3. Ensure base value is a string
                    contractorBusinessTypesEnum: String(ContractorBusinessTypesEnum.Individual),
                    taxId: "", companyName: "", suiteNo: "", streetAddress: "", city: "", state: "", zip: ""
                };

                if (defaultValues) {
                    switch (type) {
                        case "insurance":
                            Object.assign(baseValues, {
                                policyNumber: defaultValues.policyNumber,
                                insuranceValidUntil: defaultValues.validUntil?.substring(0, 10),
                                firstName: defaultValues.contactInfo?.firstName,
                                lastName: defaultValues.contactInfo?.lastName,
                                email: defaultValues.contactInfo?.email,
                                phoneNumber: defaultValues.contactInfo?.phoneNumber,
                            });

                            // Fetch insurance document if exists
                            if (defaultValues.insuranceDocumentId) {
                                const res = await DocumentService.getApiDocumentList(entityId, DocumentEntityTypesEnum.Contractor, DocumentTypesEnum.Insurance);
                                if (res?.success?.[0]) {
                                    const doc = res.success[0];
                                    setInitialInsuranceDoc({ id: doc.id!, name: doc.fileName! });
                                    fetchAndSetDocument(doc.id!, setInitialInsuranceFiles, doc.fileName!);
                                }
                            }
                            break;
                        case "license":
                            Object.assign(baseValues, {
                                licenseNumber: defaultValues.licenseNumber,
                                licenseValidUntil: defaultValues.validUntil?.substring(0, 10),
                            });

                            // Fetch license document if exists
                            if (defaultValues.licenseDocumentId) {
                                const res = await DocumentService.getApiDocumentList(entityId, DocumentEntityTypesEnum.Contractor, DocumentTypesEnum.License);
                                if (res?.success?.[0]) {
                                    const doc = res.success[0];
                                    setInitialLicenseDoc({ id: doc.id!, name: doc.fileName! });
                                    fetchAndSetDocument(doc.id!, setInitialLicenseFiles, doc.fileName!);
                                }
                            }
                            break;
                        case "tax":
                            Object.assign(baseValues, {
                                // 4. Convert incoming number to string for the form state
                                contractorBusinessTypesEnum: defaultValues.contractorBusinessTypesId 
                                    ? String(defaultValues.contractorBusinessTypesId) 
                                    : String(ContractorBusinessTypesEnum.Individual),
                                taxId: defaultValues.taxId,
                                companyName: defaultValues.companyName,
                                suiteNo: defaultValues.suiteNo,
                                streetAddress: defaultValues.address?.streetAddress,
                                city: defaultValues.address?.city,
                                // Normalize a pre-existing lowercase/full-name state value (stored
                                // before this run's strict 2-letter-uppercase requirement) to
                                // uppercase up front -- the new uppercase InputField prop only
                                // transforms typed/pasted input, never a hydrated value.
                                state: defaultValues.address?.state?.toUpperCase(),
                                zip: defaultValues.address?.zip,
                            });
                            break;
                    }
                } else {
                    // Clear initial files when not in edit mode
                    setInitialInsuranceFiles([]);
                    setInitialLicenseFiles([]);
                    setInitialInsuranceDoc(null);
                    setInitialLicenseDoc(null);
                }
                reset(baseValues);
                setSnapshot(baseValues);
                if (type === "tax" && baseValues.state) {
                    // A legacy state value that still fails the new strict 2-letter pattern (e.g.
                    // a stored full name, uppercased above but still not 2 letters) must surface
                    // its error immediately rather than silently blocking Save forever with no
                    // on-screen indication why (ui-consistency.md SS10 gotcha 7). This can't run
                    // in the same tick as reset() above -- reset() de-registers fields, and the
                    // Tax section's State InputField's useController only re-registers on the
                    // *next* render/commit, so a same-tick setValue/trigger against a
                    // not-yet-(re)registered field is a silent no-op. Deferring one macrotask past
                    // this render lets the field register first. This intentionally lives inside
                    // initializeForm (re-run on every open, per the isOpen/defaultValues deps
                    // below) rather than a separate effect keyed on detailType -- a detailType-only
                    // key does not re-fire on a close-then-reopen of the same "tax" section (the
                    // drawer's RightSideModal keeps children mounted across close/reopen, so
                    // detailType never actually changes), which silently reproduced this same
                    // symptom one interaction later.
                    const legacyState = baseValues.state;
                    setTimeout(() => {
                        setValue("state", legacyState, { shouldTouch: true, shouldValidate: true });
                    }, 0);
                }
            } else {
                // Clear initial files when closed
                setInitialInsuranceFiles([]);
                setInitialLicenseFiles([]);
                setInitialInsuranceDoc(null);
                setInitialLicenseDoc(null);
            }
        };

        initializeForm();
    }, [isOpen, defaultValues, defaultDetailType, reset, hasInsuranceDetails, hasLicenseDetails, setValue, setSnapshot]);

    // Handle document upload and return the document ID
    const handleDocumentUpload = async (file: File, docType: DocumentTypesEnum, validUntil: string): Promise<number | undefined> => {
        try {
            const documentData = {
                EntityTypeId: DocumentEntityTypesEnum.Contractor,
                EntityId: entityId,
                DocumentTypeId: docType,
                FileName: file.name,
                Document: file,
                IsMain: true,
                ValidUntil: new Date(validUntil).toISOString(),
            };
            const response = await DocumentService.postApiDocument(documentData);
            if (response.success?.id) {
                return response.success.id;
            }
            return undefined;
        } catch (error) {
            console.error("Document upload failed:", error);
            return undefined;
        }
    };

    const onSubmit = async (data: FormValues) => {
        try {
            switch (data.detailType) {
                case "insurance":
                    let insuranceDocId = 0;
                    if (data.insuranceDocument && data.insuranceDocument.length > 0) {
                        if (data.insuranceDocument[0].name === initialInsuranceDoc?.name) {
                            insuranceDocId = initialInsuranceDoc?.id || 0;
                        } else {
                            insuranceDocId = await handleDocumentUpload(data.insuranceDocument[0], DocumentTypesEnum.Insurance, data.insuranceValidUntil!);
                        }
                    }
                    const insurancePayload: ContractorInsuranceDetailsVM = {
                        policyNumber: data.policyNumber,
                        validUntil: data.insuranceValidUntil ? new Date(data.insuranceValidUntil).toISOString() : undefined,
                        insuranceDocumentId: insuranceDocId,
                        contactInfo: {
                            firstName: data.firstName,
                            lastName: data.lastName,
                            email: data.email,
                            phoneNumber: data.phoneNumber,
                        },
                    };
                    const insuranceResponse = await apiHelper(
                        ContractorService.putApiContractorInsuranceDetails,
                        { successMessage: "Insurance details saved successfully", showNotification: true, },
                        entityId,
                        insurancePayload
                    );
                    if (insuranceResponse.errors) return;
                    break;

                case "license":
                    let licenseDocId = 0;
                    if (data.licenseDocument && data.licenseDocument.length > 0) {
                        if (data.licenseDocument[0].name === initialLicenseDoc?.name) {
                            licenseDocId = initialLicenseDoc?.id || 0;
                        } else {
                            licenseDocId = await handleDocumentUpload(data.licenseDocument[0], DocumentTypesEnum.License, data.licenseValidUntil!);
                        }
                    }
                    const licensePayload: ContractorLicenseDetailsVM = {
                        licenseNumber: data.licenseNumber,
                        validUntil: data.licenseValidUntil ? new Date(data.licenseValidUntil).toISOString() : undefined,
                        licenseDocumentId: licenseDocId,
                    };
                    const licenseResponse = await apiHelper(
                        ContractorService.putApiContractorLicenseDetails,
                        { successMessage: "License details saved successfully", showNotification: true, },
                        entityId,
                        licensePayload
                    );
                    if (licenseResponse.errors) return;
                    break;

                case "tax":
                    const taxPayload: ContractorTaxDetailsVM = {
                        // 5. Convert string back to number for the API payload
                        contractorBusinessTypesId: Number(data.contractorBusinessTypesEnum) as ContractorBusinessTypesEnum,
                        taxId: data.taxId,
                        companyName: Number(data.contractorBusinessTypesEnum) === ContractorBusinessTypesEnum.Business ? data.companyName : undefined,
                        suiteNo: data.suiteNo,
                        address: {
                            streetAddress: data.streetAddress,
                            city: data.city,
                            state: data.state,
                            zip: data.zip,
                        },
                    };
                    const taxResponse = await apiHelper(
                        ContractorService.putApiContractorTaxDetails,
                        { successMessage: "Tax details saved successfully", showNotification: true, },
                        entityId,
                        taxPayload
                    );
                    if (taxResponse.errors) return;
                    break;
            }
            refreshData();
            onClose();
        } catch (error) {
            console.error("Error saving details", error);
        }
    };

    const detailTypeOptions = [
        !hasInsuranceDetails && { Id: "insurance", Name: "Insurance Details" },
        !hasLicenseDetails && { Id: "license", Name: "License Details" },
        !hasTaxDetails && { Id: "tax", Name: "Tax Details" },
    ].filter(Boolean);

    // 6. Define options for the CustomRadio component
    const businessTypeOptions = [
        { label: "Individual", value: String(ContractorBusinessTypesEnum.Individual) },
        { label: "Company", value: String(ContractorBusinessTypesEnum.Business) },
    ];

    return (
        <RightSideModal
            isOpen={isOpen}
            onClose={onClose}
            title={defaultValues ? "Edit Details" : "Add Details"}
            buttonlabel="Save"
            formId="contractorDetailsForm"
            isDirty={hasUnsavedChanges}
            isLoading={isSubmitting}
            isSubmitDisabled={isSubmitting || !hasUnsavedChanges || !isValid}
        >
            <Card className="no-bg">
                <FormProvider {...methods}>
                    <form id="contractorDetailsForm" onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            {(!defaultValues && detailTypeOptions.length > 1) && (
                                <div className="col-12"><SelectField name="detailType" label="Detail Type" required options={detailTypeOptions} /></div>
                            )}

                            <>
                                    {/* ----- INSURANCE FORM ----- */}
                                    {detailType === "insurance" && (
                                        <>
                                            <div className="col-md-12"><InputField name="policyNumber" label="Policy Number" required requiredMessage="Policy Number is required" placeholder="Enter Policy Number" validation={{ maxLength: { value: 30, message: "Policy Number cannot exceed 30 characters" } }} /></div>
                                            <div className="col-md-12"><InputField name="insuranceValidUntil" label="Valid Until" type="date" required requiredMessage="Valid Until is required" /></div>
                                            <div className="col-md-6"><InputField name="firstName" label="First Name" required requiredMessage="First Name is required" validation={{minLength: { value: 2, message: "First Name must be at least 2 characters long" }, maxLength: { value: 20, message: "First Name cannot exceed 20 characters" } }} onlyAlphabets placeholder="Enter First Name" /></div>
                                            <div className="col-md-6"><InputField name="lastName" label="Last Name" required requiredMessage="Last Name is required" validation={{minLength: { value: 2, message: "Last Name must be at least 2 characters long" }, maxLength: { value: 20, message: "Last Name cannot exceed 20 characters" } }} onlyAlphabets placeholder="Enter Last Name" /></div>
                                            <div className="col-md-6"><EmailInput name="email" label="Email Address" required requiredMessage="Email is required" placeholder="Enter Email Address" /></div>
                                            <div className="col-md-6"><PhoneInput name="phoneNumber" label="Contact No" required requiredMessage="Contact No is required" /></div>
                                            <div className="col-12 m-0">
                                                <PDFUploader name="insuranceDocument" label="Upload Insurance Document" uploaderId="insuranceDoc" allowMultiple={false} maxFiles={1} defaultFiles={initialInsuranceFiles} />
                                                {isDocLoading && <div className="text-primary mt-2">Loading insurance document...</div>}
                                            </div>
                                        </>
                                    )}

                                    {/* ----- LICENSE FORM ----- */}
                                    {detailType === "license" && (
                                        <>
                                            <div className="col-md-6"><InputField name="licenseNumber" label="License Number" required requiredMessage="License number is required" placeholder="Enter License Number" validation={{ maxLength: { value: 30, message: "License Number cannot exceed 30 characters" } }} /></div>
                                            <div className="col-md-6"><InputField name="licenseValidUntil" label="Valid Until" type="date" required requiredMessage="Valid Until is required" /></div>
                                            <div className="col-md-12">
                                                <PDFUploader name="licenseDocument" label="Upload License Document" uploaderId="licenseDoc" allowMultiple={false} maxFiles={1} defaultFiles={initialLicenseFiles} />
                                                {isDocLoading && <div className="text-primary mt-2">Loading license document...</div>}
                                            </div>
                                        </>
                                    )}
                                </>

                            {/* ----- TAX FORM ----- */}
                            {detailType === "tax" && (
                                <>
                                    {/* 7. Replaced the old radio buttons with the CustomRadio component */}
                                    <div className="col-12">
                                        <CustomRadio
                                            name="contractorBusinessTypesEnum"
                                            label="Business Type"
                                            control={control}
                                            required
                                            options={businessTypeOptions}
                                            error={errors.contractorBusinessTypesEnum?.message}
                                        />
                                    </div>

                                    <div className="col-md-12">
                                        <InputField
                                            key={selectedBusinessType}
                                            name="taxId"
                                            label={
                                                Number(selectedBusinessType) === ContractorBusinessTypesEnum.Individual
                                                    ? "Social Security Number"
                                                    : "Employee Identification Number"
                                            }
                                            placeholder={
                                                Number(selectedBusinessType) === ContractorBusinessTypesEnum.Individual
                                                    ? "Enter Social Security Number"
                                                    : "Enter Employee Identification Number"
                                            }
                                            required
                                            requiredMessage={
                                                Number(selectedBusinessType) === ContractorBusinessTypesEnum.Individual
                                                    ? "Social Security Number is required"
                                                    : "Employee Identification Number is required"
                                            }
                                            // Scoped to Individual/SSN only -- EIN format/length is unconstrained
                                            // today and out of scope for this fix (bug/service-req-contractor-plaid).
                                            onlyNumeric={Number(selectedBusinessType) === ContractorBusinessTypesEnum.Individual}
                                            validation={
                                                Number(selectedBusinessType) === ContractorBusinessTypesEnum.Individual
                                                    ? {
                                                        minLength: { value: 9, message: "Social Security Number must be 9 digits" },
                                                        maxLength: { value: 9, message: "Social Security Number must be 9 digits" },
                                                    }
                                                    : undefined
                                            }
                                        />
                                    </div>
                                    {Number(businessType) === ContractorBusinessTypesEnum.Business && (<div className="col-md-12 m-0"><InputField name="companyName" label="Company Name" required requiredMessage="Company Name is required" placeholder="Enter Company Name" /></div>)}
                                    <div className="col-md-12 m-0">
                                        <InputField
                                            name="suiteNo"
                                            label="Suite No"
                                            required
                                            requiredMessage="Suite no is required"
                                            placeholder="Enter Suite No"
                                            validation={{
                                                maxLength: { value: 50, message: "Suite no. cannot exceed 50 characters" },
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-12 m-0"><InputField name="streetAddress" label="Street Address" required requiredMessage="Street address is required" placeholder="Enter Address" /></div>
                                    <div className="col-md-4 m-0"><InputField name="city" label="City" required requiredMessage="City is required" placeholder="Enter City" validation={{ pattern: { value: /^[A-Za-z\s]+$/, message: "City must contain only letters", }, }} /></div>
                                    <div className="col-md-4 m-0"><InputField name="state" label="State" required requiredMessage="State is required" placeholder="Enter State" uppercase validation={{ pattern: { value: /^[A-Z]{2}$/, message: "Please enter the state as its 2-letter code", }, }} /></div>
                                    <div className="col-md-4 m-0">
                                        <InputField name="zip" label="ZIP Code" required requiredMessage="ZIP code is required" placeholder="Enter Zip Code"
                                            validation={{
                                                pattern: {
                                                    value: /^\d{5}$/,
                                                    message: "Zip code must be 5 digits",
                                                },
                                            }} />
                                    </div>
                                </>
                            )}
                        </div>
                    </form>
                </FormProvider>
            </Card>
        </RightSideModal>
    );
};

export default ManageContractorAdditionalDetailsForm;